import React, { useState, useEffect, useCallback } from "react";
import ReactTable from "react-table";
import Select from "react-select";
import PageTitle from "../PageTitle/page_title";
import i18next from "i18next";
import { ChecklistReportService, GroupingsService, TimeFormattingService } from "../../services";
import { DatePickerInput } from "../DatePickerInput";
import moment from "moment-timezone";
import Alert from "react-bootstrap/lib/Alert";

const ChecklistScheduleAdherenceReportForm = ({ availableGroupingValues, onSubmit }) => {
  const DATE_RANGE_OPTIONS = [
    {value: 'day', label: i18next.t('day')},
    {value: 'week', label: i18next.t('week')}
  ];

  const ALL_GROUPING = {value: -1, label: 'All', grouping: {id: -1}};

  const [dateRange, setDateRange] = useState(DATE_RANGE_OPTIONS[0]);
  const [selectedGrouping, setSelectedGrouping] = useState(null);
  const [startVariance, setStartVariance] = useState(30);
  const [endVariance, setEndVariance] = useState(30);
  const [startDate, setStartDate] = useState(null);

  const handleSubmit = useCallback((event) => {
    event.preventDefault();
    onSubmit({ dateRange: dateRange.value, selectedGrouping: selectedGrouping.value, startDate, startVariance, endVariance });
  }, [onSubmit, dateRange, selectedGrouping, startDate, startVariance, endVariance]);

  useEffect(() => {
    setStartDate(null);
  }, [dateRange])

  return (
    <div className="op-content-box filter-box" style={{marginBottom: 20}}>

      <form onSubmit={handleSubmit}>

        <ul className="list-inline clearfix">
          <li className="pull-left box-label"><h4 className="remove-margin-bottom">{i18next.t('filters')}</h4></li>
        </ul>

        <div className = "row">
          <div className="col-sm-2">
            <label>{i18next.t('timeframe')}</label>
            <Select
                options={DATE_RANGE_OPTIONS}
                clearable={false}
                value={dateRange}
                onChange={setDateRange}
                searchable={false}
                classNamePrefix="react-select"
                placeholder ={ i18next.t('select-a-timeframe') }
              />
          </div>

          <div className="pull-left" style={{"maxWidth": "150px", margin: "0px 0px 0px 10px"}}>
              <label>{ i18next.t('start-date') }</label>
              <DatePickerInput
                input={{value: startDate}}
                meta={{}}
                onDayChange={setStartDate}
                dayPickerProps={{
                    disabledDays: dateRange.value == 'day' ? {} : {daysOfWeek: [1,2,3,4,5,6]}
                }}
              />
          </div>

          <div className="col-sm-3">
            <label>{ i18next.t('location') }</label>
            <Select
                options={[ALL_GROUPING, ...availableGroupingValues]}
                clearable={false}
                value={selectedGrouping}
                onChange={setSelectedGrouping}
                classNamePrefix="react-select"
                placeholder={ i18next.t('select-a-location') }
              />
          </div>

          <div className="col-sm-2">
             <label>Start Variance In Minutes</label>
             <input 
                style={{height: 38}}
                className="form-control"
                value={startVariance}
                onChange={(event) => setStartVariance(event.target.value)}
                type="text"
             />
          </div>

          <div className="col-sm-2">
             <label>End Variance In Minutes</label>
             <input 
                style={{height: 38}}
                className="form-control"
                value={endVariance}
                onChange={(event) => setEndVariance(event.target.value)}
                type="text"
              />
          </div>

          <div className="pull-left">
            <label style={{visibility: 'hidden'}}>{ i18next.t('run-report') }</label>
            <ul className="list-inline clearfix">
              <li className="pull-left" style={{marginLeft: '15px'}}>
                <button type="submit" className="btn btn-sm btn-default pull-right" disabled={!(selectedGrouping && startDate)}>{ i18next.t('form-labels.run-report') }</button>
              </li>
            </ul>
          </div>          

        </div>
      </form>
    </div>
  );
};

const ChecklistScheduleAdherenceDayTimeCell = ({ targetDateTime, name, dayOfWeek, time }) => {
    // Monday (1) to Sunday (7)
    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return (
        <span>
            {moment(targetDateTime).utc().format("L h:mm a")} <br />
            {daysOfWeek[dayOfWeek - 1]} : {name}
        </span>
    );
};

const ChecklistScheduleAdherenceStatusCell = ({ missing, earlyBy, lateBy }) => {
    
    if (missing == true) {
        return (<span>Missing</span>);
    } else {
        return (
            <span>
                {earlyBy > 0 && `Early ${moment(earlyBy).from(0, true)}`}
                {earlyBy > 0 && <br />}
                {lateBy > 0 && `Late ${moment(lateBy).from(0, true)}`}
            </span>
        );
    }
};

const ChecklistScheduleAdherenceReportResults = ({ query, onError }) => {
    const [results, setResults] = useState(null);

    useEffect(() => {
        if (query) {
            setResults(null);

            const groupingId = query.selectedGrouping;
            const startDate = moment(query.startDate).utc().startOf("day").valueOf();
            const endDate = moment(startDate).add(1, query.dateRange).utc().valueOf();
            const startVariance = query.startVariance;
            const endVariance = query.endVariance;

            ChecklistReportService.getChecklistScheduleAdherenceReport(groupingId, startDate, endDate, startVariance, endVariance).then((response) => {
                setResults(response.data);
            }).catch((error) => {
                console.error(error);
                onError('Error fetching report.');
            });
        } else {
            setResults(null);
        }
    }, [query]);

    if (query) {
        return results ? (
            <div>
                <div className="op-content-box">
                    <ul className="list-inline clearfix">
                        <li className="pull-left box-label"><h4 className="remove-margin-bottom">{i18next.t('results')}</h4></li>
                    </ul>
                    
                    <div>
                        <div className="react-table-custom">
                            <div className="react-table-custom">
                                <ReactTable
                                    className="-striped -highlight"
                                    data={results}
                                    showPagination={false}
                                    pageSize={results.length}
                                    defaultSorted={[{
                                        id: 'locationName',
                                        desc: false // not necessary unless set to true, but it's good to know its there.
                                    }]}
                                    columns={[{
                                        Header: 'Location',
                                        accessor: "locationName",
                                        minWidth: 100,
                                        minResizeWidth: 200,
                                        width: 200
                                    }, {
                                        Header: i18next.t('table-headers.checklist'),
                                        accessor: "checklistConfigName",
                                        Cell: ({value, original}) => `${value} (V${original.checklistConfigVersion})`,
                                        minWidth: 100,
                                        minResizeWidth: 100,
                                        width: 200
                                    }, {
                                        Header: 'Scheduled For',
                                        accessor: "expectedStartTime",
                                        Cell: ({value, original}) => <ChecklistScheduleAdherenceDayTimeCell targetDateTime={value} name={original.checklistScheduleName} dayOfWeek={original.checklistScheduleDayOfWeek} time={original.checklistScheduleTime} />,
                                        minWidth: 100,
                                        minResizeWidth: 100,
                                        width: 300
                                    }, {
                                        Header: 'Status',
                                        accessor: "missing",
                                        Cell: ({value, original}) => <ChecklistScheduleAdherenceStatusCell missing={value} earlyBy={original.earlyBy} lateBy={original.lateBy} />,
                                        minWidth: 100,
                                        minResizeWidth: 100,
                                        width: 200
                                    }, {
                                        Header: 'Time',
                                        accessor: "pastChecklistStartTime",
                                        Cell: ({value, original}) => <span>
                                            Started: {value == -1 ? 'Never' : TimeFormattingService.renderNormalTime(value, original.locationTimezone)} <br/> 
                                            Ended: {original.pastChecklistEndTime == -1 ? 'Never' : TimeFormattingService.renderNormalTime(original.pastChecklistEndTime, original.locationTimezone)}
                                        </span>,
                                        minWidth: 100,
                                        minResizeWidth: 100,
                                        width: 300
                                    }]}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        ) : (
            <div className="loader">{i18next.t('loading')}</div>
        );
    } else {
        return null;
    }
};

const ChecklistScheduleAdherenceReport = () => {
    const [errorMessage, setErrorMessage] = useState(null);
    const [query, setQuery] = useState(null);
    const [availableGroupingValues, setAvailableGroupingValues] = useState(null);

    useEffect(() => {
        GroupingsService.listAll().then((response) => {
            const leafGroupings = response.groupings
                    .filter((grouping) => grouping.deviceGroup?.children?.length == 0)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((grouping) => ({ value: grouping.id, label: grouping.name, grouping: grouping }));

            setAvailableGroupingValues(leafGroupings);
        }).catch((error) => {
            setErrorMessage(i18next.t('pages.corrective-actions.loadGroupings-error-message'));
            console.error(error);
        });
    }, []);

    if (availableGroupingValues) {
        return (
            <div>
                <div className="row">
                    <PageTitle title='Schedule Adherence Report' />
                </div>
                { errorMessage && <Alert bsStyle="danger">{errorMessage}</Alert> }
                <div className="device-detail-report col-sm-12">
                    <ChecklistScheduleAdherenceReportForm availableGroupingValues={availableGroupingValues} onSubmit={setQuery} />
                </div>
                <div className = "device-detail-report col-sm-12">
                    {!errorMessage && query && <ChecklistScheduleAdherenceReportResults query={query} onError={setErrorMessage} /> }
                </div>
            </div>
        );
    } else {
        return (<div className="loader">{i18next.t('loading')}</div>);
    }
};

export default ChecklistScheduleAdherenceReport;